// Iter 387 — Detalle por auxiliar: documentos pendientes (A) + anticipos pendientes (B), selección y aplicación manual.
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { fmtFecha } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { ArrowLeft, Sparkles, Loader2, History, Link2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { AplicarPreviewDialog } from './AplicarPreviewDialog';
import { fmtCLP, fmtRut, LADO_INFO } from './regularizacionUtils';

const sum = (arr, k) => arr.reduce((s, x) => s + (Number(x[k]) || 0), 0);

export function AuxiliarWorkbench({ lado, rut, onVolver, puedeAplicar }) {
  const info = LADO_INFO[lado];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selA, setSelA] = useState(new Set());
  const [selD, setSelD] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [proponiendo, setProponiendo] = useState(false);
  const [verHist, setVerHist] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/contabilidad/regularizacion/auxiliar', { params: { lado, rut } });
      setData(r.data); setSelA(new Set()); setSelD(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cargar el auxiliar');
    } finally {
      setLoading(false);
    }
  }, [lado, rut]);
  useEffect(() => { load(); }, [load]);

  const sugPorDoc = useMemo(() => Object.fromEntries((data?.sugerencias || []).map((s) => [String(s.documento_id), s])), [data]);
  const sugPorAnt = useMemo(() => Object.fromEntries((data?.sugerencias || []).map((s) => [s.anticipo_id, s])), [data]);
  const anticipos = data?.anticipos || [];
  const documentos = data?.documentos || [];
  const antsSel = anticipos.filter((a) => selA.has(a.id));
  const docsSel = documentos.filter((d) => selD.has(d.id));

  const toggle = (set, setter, id) => setter((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const sumAnts = antsSel.reduce((s, a) => s + Math.round(Number(a.disponible) || 0), 0);
  const sumDocs = docsSel.reduce((s, d) => s + Math.round(Number(d.saldo) || 0), 0);
  const diferencia = sumAnts - sumDocs;
  const haySel = selA.size > 0 && selD.size > 0;
  const cuadra = haySel && diferencia === 0;

  const abrirPreview = async (modo) => {
    setProponiendo(true);
    try {
      const body = { lado, rut, anticipos_ids: modo === 'seleccion' ? [...selA] : [], documentos_ids: modo === 'seleccion' ? [...selD] : [] };
      const r = await api.post('/contabilidad/regularizacion/proponer', body);
      if (!r.data.pares.length) { toast.info('No hay cruces exactos (mismo monto y ≤ 20 días): selecciona anticipos y documentos que cuadren y usa "Aplicar selección"'); return; }
      setPreview({ pares: r.data.pares, anticipos: r.data.anticipos, documentos: r.data.documentos });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo preparar la aplicación');
    } finally {
      setProponiendo(false);
    }
  };

  if (loading && !data) return <div className="p-8 text-center text-sm text-muted-foreground" data-testid="wb-loading">Cargando…</div>;
  const aux = data?.auxiliar || {};

  return (
    <div className="space-y-4" data-testid="auxiliar-workbench">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onVolver} data-testid="btn-volver-aux"><ArrowLeft className="w-4 h-4 mr-1" /> Volver</Button>
          <div>
            <h2 className="text-base md:text-lg font-semibold leading-tight" data-testid="wb-nombre">{aux.nombre}</h2>
            <p className="text-xs text-muted-foreground font-mono">{fmtRut(aux.rut)} · {info.auxiliar}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setVerHist((v) => !v)} data-testid="btn-historial"><History className="w-4 h-4 mr-1" /> Historial ({data?.historial?.length || 0})</Button>
          <Button variant="outline" size="sm" onClick={() => abrirPreview('sugerido')} disabled={!puedeAplicar || proponiendo || !data?.sugerencias?.length} data-testid="btn-aplicar-sugeridos">
            <Sparkles className="w-4 h-4 mr-1 text-indigo-600" /> Revisar sugeridos ({data?.sugerencias?.length || 0})
          </Button>
          {haySel && (
            <Badge variant="outline" className={`text-[11px] tabular-nums ${cuadra ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-red-300 text-red-700 bg-red-50'}`} data-testid="sel-cuadratura">
              {cuadra ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
              {cuadra ? `Cuadra · ${fmtCLP(sumAnts)}` : `Diferencia: ${fmtCLP(diferencia)}`}
            </Badge>
          )}
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => abrirPreview('seleccion')} disabled={!puedeAplicar || proponiendo || !cuadra} data-testid="btn-aplicar-seleccion"
            title={haySel && !cuadra ? `Σ anticipos ${fmtCLP(sumAnts)} ≠ Σ documentos ${fmtCLP(sumDocs)}` : ''}>
            {proponiendo ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />} Aplicar selección
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground -mt-2" data-testid="wb-regla">
        Regla: Σ anticipos seleccionados debe ser igual a Σ documentos (pesos exactos); cada documento queda pagado completo y cada anticipo consumido completo.
        Las sugerencias solo consideran monto exacto y ≤ 20 días entre anticipo y documento.
      </p>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <section className="border rounded-lg overflow-hidden" data-testid="seccion-documentos">
          <header className="px-3 py-2 bg-rose-50 border-b flex items-center justify-between">
            <div><span className="text-sm font-semibold">A. Documentos pendientes de {info.accionDoc}</span><span className="text-xs text-muted-foreground ml-2">{documentos.length} · saldo {fmtCLP(sum(documentos, 'saldo'))}</span></div>
            {docsSel.length > 0 && <Badge variant="outline" className="text-[10px]" data-testid="docs-sel-badge">{docsSel.length} sel. · {fmtCLP(sum(docsSel, 'saldo'))}</Badge>}
          </header>
          {documentos.length === 0 ? <p className="p-4 text-xs text-muted-foreground italic">Sin {info.documento.toLowerCase()}s abiertas para este {info.auxiliar.toLowerCase()}.</p> : (
            <div className="max-h-[48vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0"><tr><th className="w-8" /><th className="text-left px-2 py-1.5">N°</th><th className="text-left px-2 py-1.5">Emisión</th><th className="text-left px-2 py-1.5">Vence</th><th className="text-right px-2 py-1.5">Total</th><th className="text-right px-2 py-1.5">Saldo</th><th className="text-left px-2 py-1.5">Sugerencia</th></tr></thead>
                <tbody>
                  {documentos.map((d) => {
                    const s = sugPorDoc[d.id];
                    return (
                      <tr key={d.id} className={`border-t cursor-pointer ${selD.has(d.id) ? 'bg-indigo-50' : ''}`} onClick={() => toggle(selD, setSelD, d.id)} data-testid={`doc-row-${d.id}`}>
                        <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}><Checkbox checked={selD.has(d.id)} onCheckedChange={() => toggle(selD, setSelD, d.id)} data-testid={`doc-check-${d.id}`} /></td>
                        <td className="px-2 py-1 font-mono">{d.numero}{d.es_honorarios && <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0 text-amber-700 border-amber-300" data-testid={`doc-honorarios-${d.id}`}>honorarios</Badge>}{d.oc && <span className="text-[10px] text-muted-foreground ml-1">OC {d.oc}</span>}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{fmtFecha(d.fecha)}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{fmtFecha(d.vencimiento)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtCLP(d.total)}</td>
                        <td className="px-2 py-1 text-right tabular-nums font-semibold">{fmtCLP(d.saldo)}</td>
                        <td className="px-2 py-1 text-[11px] text-indigo-700">{s ? <><Sparkles className="w-3 h-3 inline mr-1" />N°{anticipos.find((a) => a.id === s.anticipo_id)?.ref} · {s.motivo}</> : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="border rounded-lg overflow-hidden" data-testid="seccion-anticipos">
          <header className="px-3 py-2 bg-emerald-50 border-b flex items-center justify-between">
            <div><span className="text-sm font-semibold">B. Anticipos pendientes de regularización</span><span className="text-xs text-muted-foreground ml-2">{anticipos.length} · disponible {fmtCLP(sum(anticipos, 'disponible'))}</span></div>
            {antsSel.length > 0 && <Badge variant="outline" className="text-[10px]" data-testid="ants-sel-badge">{antsSel.length} sel. · {fmtCLP(sum(antsSel, 'disponible'))}</Badge>}
          </header>
          <div className="max-h-[48vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted sticky top-0"><tr><th className="w-8" /><th className="text-left px-2 py-1.5">N°</th><th className="text-left px-2 py-1.5">Fecha</th><th className="text-right px-2 py-1.5">Monto</th><th className="text-right px-2 py-1.5">Disponible</th><th className="text-left px-2 py-1.5">Detalle</th></tr></thead>
              <tbody>
                {anticipos.map((a) => {
                  const s = sugPorAnt[a.id];
                  const noAnt = a.es_anticipo === false;
                  return (
                    <tr key={a.id} className={`border-t ${noAnt ? 'opacity-60' : 'cursor-pointer'} ${selA.has(a.id) ? 'bg-indigo-50' : ''}`} onClick={() => !noAnt && toggle(selA, setSelA, a.id)} data-testid={`ant-row-${a.id}`}>
                      <td className="px-2 py-1" onClick={(e) => e.stopPropagation()}>{!noAnt && <Checkbox checked={selA.has(a.id)} onCheckedChange={() => toggle(selA, setSelA, a.id)} data-testid={`ant-check-${a.id}`} />}</td>
                      <td className="px-2 py-1 font-mono">{a.ref || '—'}{a.estado_aplicacion === 'parcial' && <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0">parcial</Badge>}</td>
                      <td className="px-2 py-1 whitespace-nowrap">{fmtFecha(a.fecha)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmtCLP(a.monto)}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold text-emerald-700">{fmtCLP(a.disponible)}</td>
                      <td className="px-2 py-1 max-w-[260px]">
                        <div className="truncate" title={a.descripcion}>{a.descripcion || <span className="text-muted-foreground">—</span>}{a.oc && <span className="text-[10px] text-muted-foreground ml-1">OC {a.oc}</span>}</div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {a.es_anticipo === null && <span className="text-amber-700"><AlertTriangle className="w-3 h-3 inline" /> cuenta sin verificar</span>}
                          {noAnt && <span className="text-red-700">No es anticipo · {a.cuenta}</span>}
                          {a.es_anticipo && a.cuenta && <span>{a.cuenta}</span>}
                          {s && <span className="text-indigo-700 ml-1"><Sparkles className="w-3 h-3 inline" /> → {documentos.find((d) => d.id === String(s.documento_id))?.numero}</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {verHist && (
        <section className="border rounded-lg overflow-hidden" data-testid="seccion-historial">
          <header className="px-3 py-2 bg-slate-50 border-b text-sm font-semibold">Historial de aplicaciones</header>
          {!data?.historial?.length ? <p className="p-4 text-xs text-muted-foreground italic">Sin aplicaciones registradas para este {info.auxiliar.toLowerCase()}.</p> : (
            <table className="w-full text-xs">
              <thead className="bg-muted"><tr><th className="text-left px-2 py-1.5">Fecha</th><th className="text-left px-2 py-1.5">{info.anticipo}</th><th className="text-left px-2 py-1.5">{info.documento}</th><th className="text-right px-2 py-1.5">Monto</th><th className="text-left px-2 py-1.5">Por</th><th className="text-left px-2 py-1.5">Origen</th></tr></thead>
              <tbody>
                {data.historial.map((h) => (
                  <tr key={h.id} className="border-t">
                    <td className="px-2 py-1 whitespace-nowrap">{fmtFecha(h.fecha)}</td>
                    <td className="px-2 py-1 font-mono">{h.anticipo_ref ? `N°${h.anticipo_ref}` : `Alegra #${h.alegra_payment_id}`}</td>
                    <td className="px-2 py-1 font-mono">{h.dte_folio || h.numero || `#${h.alegra_bill_id || h.alegra_invoice_id}`}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtCLP(h.monto_aplicado)}</td>
                    <td className="px-2 py-1">{h.aplicado_por}</td>
                    <td className="px-2 py-1 text-muted-foreground">{h.origen || (h.aplicacion_automatica ? 'automática (OC)' : 'manual')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {preview && <AplicarPreviewDialog lado={lado} rut={rut} pares={preview.pares} anticipos={preview.anticipos} documentos={preview.documentos} onClose={() => setPreview(null)} onDone={load} />}
    </div>
  );
}
